import { useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { PageShell } from '../components/BrandChrome'
import { Alert, Button, Card, FieldError, Input, Label, Subtitle, Title } from '../components/ui'
import { errorMessage } from '../lib/format'

export function ResetPasswordPage() {
  const [params] = useSearchParams()
  const initialToken = useMemo(() => params.get('token') ?? '', [params])
  const [token, setToken] = useState(initialToken)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.resetPassword(token.trim(), password)
      setOk(true)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <PageShell homeTo="/login">
      <Card className="mx-auto max-w-md">
        <Title>Reset password</Title>
        <Subtitle>Choose a new password for your SuretySwap creator account.</Subtitle>
        {ok ? (
          <Alert tone="success">
            Password updated. You can <Link to="/login">sign in</Link> with the new password.
          </Alert>
        ) : (
          <form className="space-y-4" onSubmit={onSubmit}>
            {error ? <Alert>{error}</Alert> : null}
            {!initialToken ? (
              <div>
                <Label htmlFor="token">Reset token</Label>
                <Input
                  id="token"
                  name="token"
                  required
                  value={token}
                  onChange={(ev) => setToken(ev.target.value)}
                />
              </div>
            ) : null}
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                aria-describedby="password-hint"
              />
              <p id="password-hint" className="mt-1 text-sm text-slate-500">
                At least 12 characters.
              </p>
              {password.length > 0 && password.length < 12 ? (
                <FieldError>Password must be at least 12 characters.</FieldError>
              ) : null}
            </div>
            <Button
              type="submit"
              disabled={busy || !token.trim() || password.length < 12}
              className="w-full"
            >
              {busy ? 'Updating…' : 'Update password'}
            </Button>
            <p className="text-center text-sm text-slate-600">
              <Link to="/login">Back to sign in</Link>
            </p>
          </form>
        )}
      </Card>
    </PageShell>
  )
}
